import React, { useState, useEffect } from 'react';
import { Search, Filter, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { registryApi } from '../../services/registryService';
import TierBadge from '../../components/registry/TierBadge';
import HealthDots from '../../components/registry/HealthDots';

const STATUS_DOT = {
  active: 'bg-emerald-500',
  deprecated: 'bg-red-400',
  planned: 'bg-blue-400',
};

export default function ServiceCatalog({ onNavigate }) {
  const [services, setServices] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total_items: 0, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('tier');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: 25,
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      if (search) params.search = search;
      if (tierFilter !== '') params.tier = tierFilter;
      if (statusFilter) params.status = statusFilter;

      const result = await registryApi.listServices(params);
      setServices(result.data || []);
      setPagination(result.pagination || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchServices(); }, [page, sortBy, sortDir, tierFilter, statusFilter]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); fetchServices(); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-amber-600" />
      : <ChevronDown className="w-3 h-3 text-amber-600" />;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Service Registry</h2>
        <p className="text-sm text-gray-500 mt-1">
          {pagination.total_items} services across the SunnyRewards platform
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search services..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none"
          />
        </div>
        <select
          value={tierFilter}
          onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 outline-none"
        >
          <option value="">All Tiers</option>
          <option value="0">Tier 0 — Critical</option>
          <option value="1">Tier 1 — Core</option>
          <option value="2">Tier 2 — Integration</option>
          <option value="3">Tier 3 — Domain</option>
          <option value="4">Tier 4 — Edge</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 outline-none"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="deprecated">Deprecated</option>
          <option value="planned">Planned</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('tier')}>
                  <div className="flex items-center gap-1">Tier <SortIcon field="tier" /></div>
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                  <div className="flex items-center gap-1">Service <SortIcon field="name" /></div>
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 hidden lg:table-cell">Capability</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Owner</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('dependency_count')}>
                  <div className="flex items-center justify-center gap-1">Deps <SortIcon field="dependency_count" /></div>
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('dependent_count')}>
                  <div className="flex items-center justify-center gap-1">Used By <SortIcon field="dependent_count" /></div>
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Health</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-gray-400">
                    <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    Loading services...
                  </td>
                </tr>
              ) : services.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-gray-400">No services found.</td>
                </tr>
              ) : (
                services.map((svc) => (
                  <tr
                    key={svc.service_id}
                    className="hover:bg-amber-50/50 cursor-pointer transition-colors"
                    onClick={() => onNavigate(`registry-detail:${svc.service_id}`)}
                  >
                    <td className="px-4 py-3"><TierBadge tier={svc.tier} /></td>
                    <td className="px-4 py-3 font-medium text-gray-900">{svc.name}</td>
                    <td className="px-4 py-3 text-gray-600 hidden lg:table-cell max-w-xs truncate">{svc.capability}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {svc.team && svc.team !== 'unknown' ? svc.team : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT[svc.status] || 'bg-gray-400'}`}
                            title={svc.status} />
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{svc.dependency_count}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{svc.dependent_count}</td>
                    <td className="px-4 py-3 flex justify-center"><HealthDots service={svc} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.total_pages} ({pagination.total_items} services)
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                disabled={page >= pagination.total_pages}
                className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
